<template>
  <BaseModule
    v-bind="$props"
    class="single-destination"
    data-module="single-destination"
  >
    <div
      v-if="(editing && !destinationId) || loading"
      class="loader-container"
    >
      <Loader v-if="loading" />
      <div
        v-if="editing && !destinationId"
        class="non-select-state-message"
      >
        Selecione um destino ➡
      </div>
    </div>
    <EmptyState
      v-else-if="!destination"
      :title="$t('notAvailableTitle')"
      :subtitle="$t('notAvailableSubtitle')"
    />
    <TextAndMedia
      v-else
      v-bind="$props"
      :subtitle="compileText(subtitle)"
      :text="compileText(text)"
      :slides="allSlides"
      :get-button-model-prop="getButtonModel"
    >
      <template #title>
        <component
          :is="titleTag"
          class="tit tit-g"
        >
          {{ compileText(title) }}
        </component>
      </template>

      <template #footer>
        <div
          v-if="showValor && destination.MenorTarifa.Valor"
          class="valor"
        >
          <div class="valor__label small-info">
            {{ $t('dailyRate') }}
            <span
              v-if="destination.MenorTarifa.TemDesconto"
              class="valor valor-promo"
            >
              {{ destination.MenorTarifa.ValorFormatadoSemDesconto }}
            </span>
          </div>
          <div
            class="half-content valor__info"
            :class="{ 'has-promo': destination.MenorTarifa.TemDesconto }"
          >
            <span class="valor">
              {{ destination.MenorTarifa.ValorFormatado }}
            </span>
          </div>
          <div
            class="half-content valor__disclaimer"
            @mouseover="showTooltip"
            @mouseleave="hideTooltip"
          >
            <span
              ref="slb-tooltip"
              class="slb-tooltip"
            >
              {{ getDisclaimer(destination) }}
            </span>
            <span class="info-icon"></span>
          </div>
        </div>
      </template>
    </TextAndMedia>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import base from '@/mixins/base';
import Loader from '@/components/Loader.vue';
import BaseModule from './generic/BaseModule.vue';
import TextAndMedia from './generic/TextAndMedia.vue';
import EmptyState from './EmptyState.vue';

export default {
  name: 'SingleDestination',
  components: {
    BaseModule,
    TextAndMedia,
    EmptyState,
    Loader,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    ...TextAndMedia.props,
    destinationId: {
      type: Number,
      default: null,
    },
    useDestinationGallery: {
      type: Boolean,
      default: true,
    },
    textPosition: {
      type: String,
      default: 'row-reverse', // overrides TextAndMedia default
    },
    showValor: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      destinations: [],
      loading: false,
    };
  },
  computed: {
    editing() {
      return this.$parent === undefined;
    },
    destination() {
      return this.destinations.find(
        (destination) => destination.Codigo === this.destinationId,
      );
    },
    allSlides() {
      const destinationGallery = [];

      if (
        this.useDestinationGallery &&
        this.destination &&
        this.destination.Imagens
      ) {
        if (this.destination.Imagens.ImagemPrincipal) {
          destinationGallery.push({
            image: this.destination.Imagens.ImagemPrincipal,
            imageMobile:
              this.destination.Imagens.ImagemPrincipalMobile ||
              this.destination.Imagens.ImagemPrincipal,
          });
        }

        if (this.destination.Imagens.Galeria) {
          this.destination.Imagens.Galeria.forEach((image) => {
            destinationGallery.push({
              image,
              imageMobile: image,
            });
          });
        }
      }

      return this.slides.concat(destinationGallery);
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    ...mapState(['apiData']),
    ...mapGetters(['settings']),
  },
  mounted() {
    this.requestDestinations();
  },
  methods: {
    compileText(text) {
      let finalText = text;

      const propertiesToReplace = {
        Cidade: '##CIDADE',
        Estado: '##ESTADO',
        Pais: '##PAIS',
        Descricao: '##DESC',
      };

      if (this.destination) {
        Object.entries(propertiesToReplace).forEach(([key, code]) => {
          if (finalText.includes(code)) {
            finalText = finalText.replaceAll(code, this.destination[key] || '');
          }
        });
      }

      return finalText;
    },
    getButtonModel({ buttonUseDestinationLink, buttonUrl }) {
      return buttonUseDestinationLink &&
        this.destination &&
        this.destination.Url
        ? this.destination.Url
        : buttonUrl;
    },
    getDisclaimer(destination) {
      const adultos = destination.MenorTarifa.NumeroAdultos;
      const noites = destination.MenorTarifa.NumeroNoites;

      return this.$t('disclaimer', {
        nights: this.$tc('night', noites),
        adults: this.$tc('adult', adultos),
      });
    },
    async requestDestinations() {
      this.loading = true;

      const urlDestinations = `${this.settings.letsbookApiId}/destinosLanding/${this.settings.letsbookLandingPageId}/${this.apiLocale}`;
      if (!this.apiData[urlDestinations]) {
        try {
          await this.$store.dispatch('loadApiData', urlDestinations);
        } catch {
          return;
        }
      }
      this.destinations = this.apiData[urlDestinations];
      this.loading = false;
    },
    showTooltip(event) {
      const el = event.target;
      const parentPosition = el.parentElement.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      if (parentPosition.bottom <= viewportHeight - 200) {
        this.$refs['slb-tooltip'].classList.add('open-down');
      } else {
        this.$refs['slb-tooltip'].classList.add('open-up');
      }
    },
    hideTooltip() {
      this.$refs['slb-tooltip'].classList.remove('open-up', 'open-down');
    },
  },
  metadata: {
    extends: [BaseModule.metadata, TextAndMedia.metadata],
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'destination',
            position: 0,
            label: 'Destino',
            helpText: `Utilize os seguintes códigos para exibir os respectivos dados do destino em qualquer campo de texto:<br>
          <ul>
            <li><b>##CIDADE</b> - Cidade do destino</li>
            <li><b>##ESTADO</b> - Estado do destino</li>
            <li><b>##PAIS</b> - País do destino</li>
            <li><b>##DESC</b> - Descrição do destino</li>
          <ul>`,
            fields: [
              {
                name: 'destinationId',
                position: 0,
                type: 'VueSelect',
                label: 'Selecione o destino',
                options() {
                  const destinationsToOptions = (destinations) =>
                    destinations.map((destination) => ({
                      label: destination.Cidade,
                      value: destination.Codigo,
                    }));

                  // Are destinations already loaded?
                  if (this.destinations.length > 0) {
                    return destinationsToOptions(this.destinations);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch(
                      'destinations',
                      (destinations) => {
                        resolve(destinationsToOptions(destinations));
                        unwatch();
                      },
                    );
                  });
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Selecione um destino',
                  },
                },
              },
              {
                name: 'useDestinationGallery',
                after: 'destinationId',
                type: 'Checkbox',
                label: 'Mostrar galeria do destino',
              },
            ],
          },
          {
            name: 'buttons',
            fields: [
              {
                name: 'buttons',
                fields: [
                  {
                    name: 'buttonUseDestinationLink',
                    before: 'buttonUrl',
                    type: 'Checkbox',
                    label: 'Usar link do destino no Letsbook',
                  },
                  {
                    name: 'buttonUrl',
                    isEnabled() {
                      return !this.buttonUseDestinationLink;
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            fields: [
              {
                name: 'showValor',
                type: 'Checkbox',
                label: 'Mostrar preços',
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        dailyRate: 'Hospedagem a partir de',
        adult: '1 adulto | {count} adultos',
        night: '1 diária | {count} diárias',
        disclaimer:
          'Valor da diária baseado em uma pesquisa de {nights} para {adults}',
        notAvailableTitle: 'Ops, não encontramos nenhum destino disponível.',
        notAvailableSubtitle:
          'Em breve listaremos aqui todas os nossos destinos para você escolher a que mais se encaixa no seu perfil.',
      },
      en: {
        dailyRate: 'Room rates from',
        adult: '1 adult | {count} adults',
        night: '1-night | {count}-nights',
        disclaimer: 'Room rates based on a {nights} stay for {adults}',
        notAvailableTitle: 'Ops, we did not find any destination.',
        notAvailableSubtitle:
          "We'll soon list all our destinations here so that you can find the one that best suits your profile.",
      },
      es: {
        dailyRate: 'Tarifas a partir de',
        adult: '1 adulto | {count} adultos',
        night: '1 noche | {count} noches',
        disclaimer:
          'Valor de la diaria basado en una busca de {nights} para {adults}',
        notAvailableTitle: 'Ops, no encontramos habitaciones disponibles.',
        notAvailableSubtitle:
          'En breve enumeraremos aquí todos nuestros alojamientos para que elijas el que mejor se adapta a tu perfil.',
      },
    },
  },
};
</script>

<style lang="scss">
.single-destination {
  position: relative;

  .loader-container {
    width: 100%;
    text-align: center;
    padding: 96px 20px;

    .loader + .non-select-state-message {
      margin-top: 30px;
    }

    .non-select-state-message {
      font-size: 20px;
      font-weight: $lb-semibold;
      font-family: $lb-setting-font;
    }
  }

  .valor {
    min-width: 200px;
    margin-right: 12px;
    padding: 4px 0;

    &__label {
      text-transform: uppercase;
      font-size: $size-normal;

      .valor-promo {
        display: block;
        color: $lb-gray;
        margin-bottom: 0.2em;
        text-decoration: line-through;
      }
    }

    &__info {
      font-weight: $lb-bold;
      text-transform: none;
      color: var(--primary-color);

      .valor {
        font-size: 26px;
        color: inherit;
      }
    }

    .has-promo {
      color: var(--promo-color);
    }

    &__disclaimer {
      margin-left: 15px;
      position: relative;

      .slb-tooltip {
        right: -10px;
        visibility: hidden;
        opacity: 0;
        color: $lb-dark-gray;

        &.open-up,
        &.open-down {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
      }
    }
  }

  .half-content {
    display: inline-block;
    vertical-align: middle;
  }

  .info-icon {
    line-height: 1em;
  }
}
</style>
